import { Card, FormGroup, FormSection } from '@ksyos/design-system';
import { useContext, useEffect, useState } from 'react';
import type { IService } from '@common/interfaces';
import type { SettingName, SettingType } from '@common/utils/settings';
import { addRightsSettingsSchema } from '@common/validation/addCarePath';
import { SettingsSwitch } from '@frontend/components/pages/carePath/modals/components/SettingsSwitch';
import { translateKey } from '@frontend/translate';
import type { IServiceEmploymentSetting, IServiceEmploymentSettingList } from '../utils/serviceEmploymentSettingsUtils';
import { CareProviderAddRightsContext } from '../careProviderAddRightsContext';
import { getServiceEmploymentSettingList } from '../utils/serviceEmploymentSettingsUtils';
import { validateOneAddRights } from '../utils/validateOne';

interface IServiceEmploymentSettingsProps {
    service: IService;
}

export function ServiceEmploymentSettings(props: IServiceEmploymentSettingsProps) {
    const { service } = props;
    const { addRightsState, dispatchAddRightsAction } = useContext(CareProviderAddRightsContext);

    const [settingList, setSettingList] = useState<IServiceEmploymentSettingList>();

    useEffect(() => {
        const serviceActivityCodes = addRightsState.serviceEmploymentActivities
            .filter((item) => item.id === service.id)[0]
            .serviceActivities.map((activity) => activity.customControlCode);

        const serviceEmploymentSettingsList = getServiceEmploymentSettingList(serviceActivityCodes);

        setSettingList(serviceEmploymentSettingsList);
    }, [addRightsState.serviceEmploymentActivities]);

    return (
        <>
            <Card>
                <Card.Header title={`${translateKey(`common.settings`)} ${service.name}`} />
                <Card.Body>{renderSettingList()}</Card.Body>
            </Card>
        </>
    );

    function renderSettingList() {
        if (settingList) {
            const settingSectionHeaders = [...new Set(Object.entries(settingList).map(([_, item]) => item.header))];

            return settingSectionHeaders.map((settingSection) => {
                const settingsPerSection = Object.entries(settingList).filter(
                    ([, item]) => item.header === settingSection
                );

                return (
                    <FormSection
                        key={`settingSection-${settingSection}`}
                        title={settingSection}
                    >
                        {settingsPerSection.map(([settingName, settingProps]) => (
                            <FormGroup
                                key={`settingLabel-${settingProps.label}`}
                                label={settingProps.label}
                            >
                                {renderSetting(settingName as SettingName, settingProps, true)}
                            </FormGroup>
                        ))}
                    </FormSection>
                );
            });
        }
    }

    function renderSetting(settingName: SettingName, settingProps: IServiceEmploymentSetting, isEditMode: boolean) {
        switch (settingProps.header) {
            case translateKey('addCarePathSettingsHeaders.startRequest'):
                return (
                    <SettingsSwitch
                        listKey={settingName}
                        onChange={(value) => onHandleChange(settingName, value)}
                        value={false}
                    />
                );

            default:
                return (
                    <SettingsSwitch
                        listKey={settingName}
                        onChange={(value) => onHandleChange(settingName, value)}
                        value={false}
                    />
                );
        }
    }

    function onHandleChange(name: SettingName, value: SettingType) {
        const currentSettings = addRightsState.serviceEmploymentActivities.filter((item) => item.id === service.id)[0]
            .settings;

        const updatedSettings = {
            ...currentSettings,
            [name]: value,
        } as Partial<SettingType>;

        dispatchAddRightsAction({
            type: 'updateServiceEmploymentSettings',
            serviceId: service.id,
            settings: updatedSettings,
        });
        validateChanges(updatedSettings);
    }

    function validateChanges(settings: Partial<SettingType>) {
        validateOneAddRights(
            addRightsSettingsSchema,
            settings,
            'serviceEmploymentActivitiesSettings',
            addRightsState.errors,
            dispatchAddRightsAction
        );
    }
}
